import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Trash2, Check, Loader2 } from 'lucide-react';
import { SiteLogoManagementDialog } from './SiteLogoManagementDialog';

interface SiteLogo {
  id: string;
  name: string;
  image_url: string;
  alt_text: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export function SiteLogoManagement() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLogo, setSelectedLogo] = useState<SiteLogo | null>(null);
  const [activatingId, setActivatingId] = useState<string | null>(null);

  const { data: logos, isLoading } = useQuery({
    queryKey: ['admin-site-logos'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_logos')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as SiteLogo[];
    },
  });

  const activateMutation = useMutation({
    mutationFn: async (logoId: string) => {
      // Deactivate all logos first
      const { error: deactivateError } = await supabase
        .from('site_logos')
        .update({ is_active: false })
        .neq('id', logoId);
      if (deactivateError) throw deactivateError;

      // Activate the selected logo
      const { error: activateError } = await supabase
        .from('site_logos')
        .update({ is_active: true })
        .eq('id', logoId);
      if (activateError) throw activateError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-site-logos'] });
      queryClient.invalidateQueries({ queryKey: ['active-site-logo'] });
      toast.success('Logo activated successfully');
      setActivatingId(null);
    },
    onError: (error) => {
      toast.error(`Failed to activate logo: ${error.message}`);
      setActivatingId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (logoId: string) => {
      const { error } = await supabase
        .from('site_logos')
        .delete()
        .eq('id', logoId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-site-logos'] });
      queryClient.invalidateQueries({ queryKey: ['active-site-logo'] });
      toast.success('Logo deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedLogo(null);
    },
    onError: (error) => {
      toast.error(`Failed to delete logo: ${error.message}`);
    },
  });

  const handleActivate = (logo: SiteLogo) => {
    if (logo.is_active) return;
    setActivatingId(logo.id);
    activateMutation.mutate(logo.id);
  };

  const handleEdit = (logo: SiteLogo) => {
    setSelectedLogo(logo);
    setDialogOpen(true);
  };

  const handleDelete = (logo: SiteLogo) => {
    if (logo.is_active) {
      toast.error('Cannot delete the active logo. Please activate another logo first.');
      return;
    }
    setSelectedLogo(logo);
    setDeleteDialogOpen(true);
  };

  const handleAdd = () => {
    setSelectedLogo(null);
    setDialogOpen(true);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Site Logo Management</CardTitle>
            <CardDescription>
              Upload and manage seasonal logos. Click "Activate" to display a logo site-wide.
            </CardDescription>
          </div>
          <Button onClick={handleAdd}>
            <Plus className="mr-2 h-4 w-4" />
            Add Logo
          </Button>
        </CardHeader>
        <CardContent>
          {logos && logos.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">Preview</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logos.map((logo) => (
                  <TableRow key={logo.id}>
                    <TableCell>
                      <div className="h-12 w-24 bg-muted rounded flex items-center justify-center p-1">
                        <img
                          src={logo.image_url}
                          alt={logo.alt_text}
                          className="h-full object-contain"
                        />
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{logo.name}</TableCell>
                    <TableCell>
                      {logo.is_active ? (
                        <Badge className="bg-green-500 hover:bg-green-600">
                          <Check className="mr-1 h-3 w-3" />
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="outline">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {!logo.is_active && (
                          <Button
                            size="sm"
                            onClick={() => handleActivate(logo)}
                            disabled={activatingId === logo.id}
                          >
                            {activatingId === logo.id ? (
                              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                            ) : (
                              <Check className="mr-1 h-3 w-3" />
                            )}
                            Activate
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(logo)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(logo)}
                          disabled={logo.is_active}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No logos found. Add your first logo to get started.
            </div>
          )}
        </CardContent>
      </Card>

      <SiteLogoManagementDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        logo={selectedLogo}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Logo</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedLogo?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => selectedLogo && deleteMutation.mutate(selectedLogo.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
